import React, {useState, useMemo, useCallback, useEffect} from "react";
import { useParams } from 'react-router-dom';
import EditSiloGroupHeader from "./components/EditSiloGroupHeader";
import EditSiloGroupChannelMapping from "./components/EditSiloGroupChannelMapping";
import SiloPositionVisualizer from "./components/SiloPositionVisualizer";
import {useCurrentSelection} from "../../hooks/useCurrentSelection";
import {silogroupAC} from "../../store/branches/silo-group/actionCreators";
import {useDispatch, useSelector} from "react-redux";
import {selectSensorsState, selectSiloGroupsState} from "../../store/selectors";
import { Form } from "antd";
import {sensorsAC} from "../../store/branches/sensors/actionCreators";
import SiloGroupAlert from "../Alerts/silogroup";

const POSITIONS_OPTIONS = Array.from({ length: 8 }, (_, i) => ({
    value: `cable_${i + 1}`,
    label: `Position ${i + 1}`,
}));


const EditSiloGroup: React.FC = () => {
    const params = useParams() as { id: string };
    const urlId = Number(params.id);
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const {device} = useCurrentSelection();
    const {sensorNames} = useSelector(selectSensorsState);
    const [availablePositions, setAvailablePositions] = useState(POSITIONS_OPTIONS);
    const { configData, status,groupTypes } = useSelector(selectSiloGroupsState);
    const [activePosition, setActivePosition] = useState(null);
    const initialDeviceConfigs = configData?.configuration_data  || []; // Fallback to mock channel list

    const [deviceConfigs, setDeviceConfigs] = useState(initialDeviceConfigs);

    // Safely pull siloName for the Header
    const [siloName, setSiloName] = useState(configData?.name || "");
    const [selectedGroupType, setSelectedGroupType] = useState(configData?.silo_type?.id || null);

    const createInitialChannelState = (sensorNames) => {
        return sensorNames.sensor_names.map(channel => ({
            channelId: channel.id,
            channelName: channel.name,
            isEnabled: true,
            mappedPosition: channel.id <= availablePositions.length ? channel.id : null,
        }));
    };

    const createSiloPositions = (numberOfLocations) => {
    return Array.from({ length: numberOfLocations }, (_, i) => ({
        // Ensure values are strings for dropdown consistency
        value: `cable_${i + 1}`,
        label: `Position ${i + 1}`,
    }));
};

    const totalLocations = useMemo(() => {
        const type =groupTypes?.find(type => type.id === selectedGroupType);

        return type ? type.number_of_locations : POSITIONS_OPTIONS.length;
    }, [selectedGroupType, groupTypes]);

useEffect(() => {
    // RESET EVERYTHING when the URL ID changes
    setDeviceConfigs([]);
    setSiloName("");
    setSelectedGroupType(null);
    setActivePosition(null);
    form.resetFields();
}, [urlId, form]);

    useEffect(() => {
        if (!sensorNames) {
            dispatch(sensorsAC.fetchSensorNames(device?.id.toString()));
        }
    }, [dispatch, sensorNames]);

    useEffect(() => {
        if (!groupTypes) {
            dispatch(silogroupAC.fetchSiloGroupTypes());
        }
    }, [dispatch, groupTypes]);

    useEffect(() => {
    if (configData && status === 'LOADED') {
        setDeviceConfigs(configData.configuration_data);
    }
}, [configData, status, form]);

    // --- EFFECT 1: Load Data on Mount (Fetch Request) ---
    useEffect(() => {
        // Fetch only if Redux store doesn't have data yet and devicePk is known
        if (!isNaN(urlId)) {
            // Dispatch action to fetch configuration data from the API
            dispatch(silogroupAC.fetchSiloConfigRequest(urlId));
        }
    }, [urlId, dispatch]);

    // --- EFFECT 2: Sync Local State when Redux Data Arrives ---
    useEffect(() => {
        const dataMatchesUrl = configData?.devices?.includes(urlId);

        if (configData && status === 'LOADED' && dataMatchesUrl) {
            const fetchedDeviceConfigs = configData.configuration_data;

            // 1. Sync Channel Configuration Array
            if (Array.isArray(fetchedDeviceConfigs) && fetchedDeviceConfigs.length > 0) {
                 setDeviceConfigs(fetchedDeviceConfigs);
            }

            // 2. Sync Group Name (Header Form)
            if (configData.name) {
                setSiloName(configData.name);

            }
              if (configData.silo_type && groupTypes) {

                setSelectedGroupType(configData.silo_type.id);
                 const newPositions = createSiloPositions(configData.silo_type.number_of_locations);
                 setAvailablePositions(newPositions);
            }
        }
        else if (!configData && sensorNames  && deviceConfigs.length === 0 && status === 'LOADED') {
            setDeviceConfigs([{
                Id: urlId,
                deviceId: device.title,
                deviceAlias: 'Inner Layer',
                channels: createInitialChannelState(sensorNames),
            }]);
        }
        else if (configData && !dataMatchesUrl && status === 'LOADED') {
         setDeviceConfigs([]);
        }
    }, [configData, status,sensorNames, form]);

    useEffect(() => {
        form.setFieldsValue({
            "Device Group Name": siloName || "",
            groupTypeID: selectedGroupType || undefined,
        });
    }, [form, siloName, selectedGroupType]);


    // --- HANDLERS (LIFTED STATE) ---
    const handleChannelEnableToggle = useCallback((deviceId, channelId) => {
        setDeviceConfigs(prevConfigs => prevConfigs.map(device => {
            if (device.deviceId === deviceId) {
                const newChannels = device.channels.map(channel => {
                    if (channel.channelId === channelId) {
                        return { ...channel, isEnabled: !channel.isEnabled };
                    }
                    return channel;
                });
                return { ...device, channels: newChannels };
            }
            return device;
        }));
    }, []);

    const handlePositionChange = useCallback((deviceId, channelId, newPositionValue, newPositionText) => {

        setDeviceConfigs(prevConfigs => prevConfigs.map(device => {
            if (device.deviceId === deviceId) {
                const newChannels = device.channels.map(channel => {
                    if (channel.channelId === channelId) {
                        return { ...channel, mappedPosition: newPositionValue, positionName: newPositionText };
                    }
                    return channel;
                });
                return { ...device, channels: newChannels };
            }
            return device;
        }));
    }, []);

    // --- SAVE HANDLER (Dispatch Save Action) ---
    const handleSaveSiloConfig = (values) => {
        // 1. Construct the final payload object matching the API response structure

      const selectedType = groupTypes?.find(type => type.id === values.groupTypeID);
        if (!selectedType) {
            return;
        }

        const finalPayload = {
            id: configData?.id,
            name: values['Device Group Name'],
            configuration_data: deviceConfigs,
            silo_type_id:selectedType.id
        };

        dispatch(silogroupAC.saveSiloConfigRequest(device.id,finalPayload));
    };

    const handleSiloNameChange = useCallback((value: string) => {
        setSiloName(value);
    }, []);

    const handleSetActivePosition = useCallback((positionValue) => {
        setActivePosition(positionValue);
    }, []);

    const handleSelectGroupType= useCallback((value: any) => {
        setSelectedGroupType(value);
        const selectedType = groupTypes?.find(type => type.id === value);
        if (selectedType) {
            const numLocations = selectedType.number_of_locations;
            const newPositions = createSiloPositions(numLocations);
            setAvailablePositions(newPositions);
            setActivePosition(null);
        }
    }, [groupTypes]);

    // --- UTILITY: Get occupied positions to prevent conflicts ---
    const getOccupiedPositions = useMemo(() => {
        const occupied = new Set();

          deviceConfigs.forEach(device => {
            device.channels.forEach(channel => {
                if (channel.isEnabled && channel.mappedPosition) {
                    occupied.add(channel.mappedPosition);
                }
            });
        });
        return occupied;
    }, [deviceConfigs]);



const isDataReady = useMemo(() => {

    if (deviceConfigs.length > 0) {
        return true;
    }

   if (configData) {
        return configData.devices?.includes(urlId);
    }
    if (status === 'LOADED' && !configData) {
        return deviceConfigs.length > 0;
    }

    return false;
}, [configData, urlId, status, deviceConfigs.length]);

    // Show loading spinner if data is not ready
   if (status === 'LOADING' || !isDataReady) {
        return <div style={{ padding: '50px', textAlign: 'center' }}>Loading Configuration...</div>;
    }

    return (

        <div style={{minHeight: '100vh', backgroundColor: '#f9f9f9', padding: '30px'}}>
            <SiloGroupAlert/>
            <h1 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#333',
                marginBottom: '20px',
                borderBottom: '1px solid #ddd',
                paddingBottom: '10px'
            }}>
                Silo Sensor Mapping Configuration
            </h1>

            {/* 1. TOP SETTINGS BLOCK */}
            <EditSiloGroupHeader
                siloName={siloName}
                deviceCount={deviceConfigs.length}
                groupTypes={groupTypes}
                onSave={handleSaveSiloConfig}
                handleSelectGroupType={handleSelectGroupType}
                selectedGroupType={selectedGroupType}
                onSiloNameChange={handleSiloNameChange}
                form={form}
            />

            {/* 2. DEVICE MAPPING SECTION */}

            <h2 style={{fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '15px'}}>Device Channel
                Assignment</h2>
            <div style={{display: 'flex', gap: '30px', alignItems: 'flex-start'}}>
                <div style={{flex: '0.9', minWidth: '400px'}}>
                    <div style={{marginBottom: '20px'}}>
                        {/* Map over all devices and render a mapping card for each */}
                        {deviceConfigs.map(device => (
                            <EditSiloGroupChannelMapping
                                key={device.deviceId}
                                device={device}
                                POSITIONS_OPTIONS={availablePositions}
                                getOccupiedPositions={getOccupiedPositions}
                                onChannelEnableToggle={handleChannelEnableToggle}
                                onPositionChange={handlePositionChange}
                                onPositionFocus={handleSetActivePosition}
                            />
                        ))}
                    </div>
                </div>
                    <div style={{flex: '0.4', height: '500px', position: 'sticky', top: '20px'}}>
                        <SiloPositionVisualizer
                            totalLocations={totalLocations}
                            occupiedPositions={getOccupiedPositions}
                            activePosition={activePosition}
                             selectedGroupType={selectedGroupType}
                        />
                    </div>
                </div>
            </div>

            )
            ;
            };
            export default EditSiloGroup;